import React, { useState } from "react";
import useSWR from "swr";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { StatusBadge } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { Plus, X } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function ResellersPage() {
    const { data, mutate, isLoading } = useSWR("/api/admin/resellers", fetcher);
    const nav = useNavigate();
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", slug: "", contact_email: "" });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            await api.post("/api/admin/resellers", form);
            setForm({ name: "", slug: "", contact_email: "" });
            setShowForm(false);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div data-testid="resellers-page">
            <PageHeader
                testid="resellers-header"
                title="Resellers"
                subtitle="Multi-tenant operators of NexTerminal. Each owns clouds, tokens and a usage book."
                action={
                    <button
                        data-testid="resellers-new-button"
                        className="cm-btn-primary flex items-center gap-2"
                        onClick={() => setShowForm((v) => !v)}
                    >
                        {showForm ? <X size={14} /> : <Plus size={14} />}
                        {showForm ? "Cancel" : "New reseller"}
                    </button>
                }
            />

            {showForm && (
                <form
                    onSubmit={submit}
                    className="mx-4 sm:mx-6 mt-4 cm-card p-5 space-y-3 max-w-2xl cm-fade-in"
                    data-testid="resellers-form"
                >
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="cm-label">Name</label>
                            <input
                                data-testid="resellers-form-name"
                                className="cm-input mt-1"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <label className="cm-label">Slug</label>
                            <input
                                data-testid="resellers-form-slug"
                                className="cm-input mt-1 font-mono"
                                value={form.slug}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                                    })
                                }
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="cm-label">Contact email</label>
                        <input
                            data-testid="resellers-form-email"
                            type="email"
                            className="cm-input mt-1"
                            value={form.contact_email}
                            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                        />
                    </div>
                    {err && <div className="text-xs text-cm-danger">{err}</div>}
                    <button
                        type="submit"
                        disabled={busy}
                        className="cm-btn-primary"
                        data-testid="resellers-form-submit"
                    >
                        {busy ? "Creating…" : "Create reseller"}
                    </button>
                </form>
            )}

            <div className="p-4 sm:p-6 pt-4">
                {isLoading ? (
                    <Spinner />
                ) : !data?.length ? (
                    <Empty
                        title="No resellers yet"
                        hint="Create your first reseller to start onboarding tenants."
                        testid="resellers-empty"
                    />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Name</th>
                                    <th className="cm-th">Slug</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">Clouds</th>
                                    <th className="cm-th">Tokens</th>
                                    <th className="cm-th">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="cm-tr"
                                        data-testid={`reseller-row-${r.id}`}
                                        onClick={() => nav(`/resellers/${r.id}`)}
                                    >
                                        <td className="cm-td">
                                            <Link
                                                to={`/resellers/${r.id}`}
                                                className="text-zinc-100 hover:text-[#CCFF00]"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {r.name}
                                            </Link>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">{r.slug}</td>
                                        <td className="cm-td">
                                            <StatusBadge status={r.status} />
                                        </td>
                                        <td className="cm-td font-mono">{r.clouds_count}</td>
                                        <td className="cm-td font-mono">{r.tokens_count}</td>
                                        <td className="cm-td text-xs text-zinc-500">{fmtRel(r.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
